import { useMutation, useQuery } from '@apollo/client';
import { EyeIcon, TrashIcon, UserCircleIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/buttons/Button';
import Modal from '../../../components/modal/Modal';
import Table from '../../../components/table/Table';
import { useToast } from '../../../components/toast/ToastProvider';
import { Link, Subtitle, Title } from '../../../components/typography';
import { DELETE_JOB } from '../../../graphql/jobs/mutations';
import { GET_JOB, GET_JOBS } from '../../../graphql/jobs/queries';
import Form from './sharedComponents/Form';

function Jobs() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data } = useQuery(GET_JOBS);
  const [open, setOpen] = useState(false);
  const [deleteJob] = useMutation(DELETE_JOB);
  const { data: job } = useQuery(GET_JOB, {
    variables: { _id }
  });

  const handleClick = async (id) => {
    const res = await deleteJob({
      variables: { _id: id },
      refetchQueries: [{ query: GET_JOBS }, 'GetJobs']
    });

    toast({
      title: res.data?.deleteJob?.error ? 'Job title not deleted' : 'Job title deleted',
      description:
        res.data?.deleteJob?.message ||
        `The job title ${res.data?.deleteJob?.name} has been deleted successfully.`,
      type: res.data?.deleteJob?.error ? 'error' : 'success'
    });
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'Name'
      },
      {
        accessor: 'createdBy',
        Header: 'Creator',
        Cell: ({ value }) => {
          return <Link href={`mailto:${value}`} text={value} />;
        }
      },
      {
        accessor: 'createdAt',
        Header: 'Date',
        Cell: ({ value }) => {
          return <>{dayjs(value).format('LLL')}</>;
        }
      },
      {
        accessor: 'actions',
        Header: '',
        Cell: ({ row }) => {
          return (
            <div className="flex items-center justify-center space-x-2">
              <Button
                type="button"
                color="text-red-500"
                Icon={TrashIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  toast({
                    type: 'warning',
                    title: 'Delete job title',
                    autoClose: false,
                    description: 'Are you sure you want to delete this job title?',
                    onAccept: () => handleClick(row.original._id)
                  });
                }}
              />
              <Button
                type="button"
                color="text-sky-500"
                Icon={EyeIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/employees/jobs/${row.original._id}`);
                }}
              />
            </div>
          );
        },
        disableSortBy: true
      }
    ],
    []
  );

  useEffect(() => {
    if (_id) setOpen(true);
  }, [_id]);

  return (
    <>
      <Modal
        open={open}
        closeModal={() => setOpen(false)}
        title="Job title Form"
        description="Use the following request form to create or edit a job title."
        onExitComplete={() => {
          if (_id) {
            navigate('/employees/jobs/');
          }
        }}
      >
        <Form onSuccess={() => setOpen(false)} data={job?.job} />
      </Modal>
      <Title text="Job titles" />
      <Subtitle text="This section is intended for managing employees job titles. Please use this section to create, read, update or delete a job title." />
      <Table
        columns={columns}
        data={data?.jobs || []}
        onRowClick={(row) => navigate(`/employees/jobs/${row?._id}`)}
        toolbar={
          <div className="flex items-center">
            <Button
              label="New Position"
              Icon={UserCircleIcon}
              backgroundColor="bg-green-500"
              onClick={() => setOpen(true)}
            />
          </div>
        }
      />
    </>
  );
}

export default Jobs;
